import { isNumberString } from "@corca/core";

import { containsSpecialCharacter } from "@/utils/containsSpecialCharacter";

import { constants } from "./constantSuggestionsProvider";
import { expressionSuggestions } from "./expressionSuggestionsProvider";
import { functions } from "./functionSuggestionsProvider";
import { createSuggestionFromNode } from "./utils";
import { matchesKeyword } from "./utils/filterSuggestions";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";
import { variables } from "./variableSuggestionsProvider";

import type { IconName } from "@/components/icons/Icon";
import type {
  SingleSuggestion,
  SuggestionNodeType,
  SuggestionProvider,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { SuggestionIntlName } from "@/models/nodes/types";
import type { KeywordLang } from "@corca/data";

function findJsonMatch<T extends SuggestionNodeType>(
  list: SingleSuggestion<T>[],
  str: string,
  lang: KeywordLang,
): SingleSuggestion<T> | undefined {
  return list.find(({ item }) => item.symbol === str || matchesKeyword(item, str, lang));
}

function findDocNode<T extends "variable" | "constant">(
  doc: DocumentModel,
  type: T,
  str: string,
): NodeModel<T> | undefined {
  const groups = doc.nodeLookup?.groupByKey({
    filter: (base, nodeType) => nodeType === type && base === str,
  });

  if (!groups) {
    return undefined;
  }

  const [group] = groups.values();
  const [nodeId] = group?.ids ?? [];
  return nodeId ? (doc.nodes.get(nodeId) as NodeModel<T> | undefined) : undefined;
}

function getSuggestion<T extends SuggestionNodeType>(
  suggestion: SingleSuggestion<T>,
  intlName: string,
  icon: IconName,
): SingleSuggestion<T> {
  return {
    ...suggestion,
    item: {
      ...suggestion.item,
      intlName: intlName as SuggestionIntlName,
      displayName: undefined,
      category: undefined,
    },
    icon,
    trackingId: intlName,
    nodePreviewPlacement: "afterTitle",
    priority: 2,
    popupGroup: "convertNode",
    nestedKey: undefined,
  };
}

function buildVariableSuggestion(
  str: string,
  doc: DocumentModel,
  lang: KeywordLang,
): SingleSuggestion<"variable"> {
  const docNode = findDocNode(doc, "variable", str);
  if (docNode) {
    return getSuggestion(createSuggestionFromNode(docNode), "convertToVariable", "CreateVariable");
  }

  const jsonMatch = findJsonMatch(variables, str, lang);
  if (jsonMatch) {
    return getSuggestion(jsonMatch, "convertToVariable", "CreateVariable");
  }

  return {
    type: "single",
    item: {
      type: "variable",
      symbol: str,
      isNewNode: true,
      keywords: normalizeSuggestionKeywords({}),
      intlName: "createVariableOrFunction" as SuggestionIntlName,
    },
    icon: "CreateVariable",
    trackingId: "convertNode",
    nodePreviewPlacement: "afterTitle",
    priority: 2,
    popupGroup: "convertNode",
  };
}

function buildFunctionSuggestion(
  str: string,
  lang: KeywordLang,
): SingleSuggestion<SuggestionNodeType> | undefined {
  const fnMatch = findJsonMatch(functions, str, lang);
  if (fnMatch) {
    return getSuggestion(fnMatch, "convertToFunction", "CreateFunction");
  }

  const exprMatch = findJsonMatch(expressionSuggestions, str, lang);
  if (exprMatch) {
    return getSuggestion(exprMatch, "convertToFunction", "CreateFunction");
  }

  return undefined;
}

function buildConstantSuggestion(
  str: string,
  doc: DocumentModel,
  lang: KeywordLang,
): SingleSuggestion<"constant"> | undefined {
  const docNode = findDocNode(doc, "constant", str);
  if (docNode) {
    return getSuggestion(createSuggestionFromNode(docNode), "convertToConstant", "CreateConstant");
  }

  const jsonMatch = findJsonMatch(constants, str, lang);
  if (jsonMatch) {
    return getSuggestion(jsonMatch, "convertToConstant", "CreateConstant");
  }

  return undefined;
}

function isConvertibleWord(str: string): boolean {
  return (
    str.length < 20 && /^\S*$/.test(str) && !isNumberString(str) && !containsSpecialCharacter(str)
  );
}

export const convertNodeSuggestionsProvider: SuggestionProvider<"convertNode", SuggestionNodeType> =
  {
    type: "convertNode",
    search: (str, doc, lang) => {
      if (
        !doc.selection.hasTextSelection ||
        doc.editor.editorState.isDocumentTitle ||
        doc.editor.editorState.isHeading ||
        !str ||
        !isConvertibleWord(str)
      ) {
        return [];
      }

      const functionSuggestion = buildFunctionSuggestion(str, lang);
      if (functionSuggestion) {
        return [[functionSuggestion]];
      }

      const constantSuggestion = buildConstantSuggestion(str, doc, lang);
      if (constantSuggestion) {
        return [[constantSuggestion]];
      }

      return [[buildVariableSuggestion(str, doc, lang)]];
    },
  };
